import React, { useMemo } from 'react';
import { Trash2, ChevronDown, ChevronUp, Loader, CheckCircle, AlertCircle } from 'lucide-react';
import StatusPanel from '../../StatusPanel';

/** Stable reference for React.memo — avoids inline object identity churn on parent re-renders */
const EXPANDED_PANEL_STYLE = Object.freeze({ maxHeight: 420 });

function FloatingProcessingLogPanel({
  expanded,
  onExpand,
  onCollapse,
  onClear,
  logs,
  isProcessing,
  hasError,
  isComplete,
  collapsedPreviewText
}) {
  const statusIcon = useMemo(() => {
    const showSuccess = !isProcessing && logs.length > 0 && !hasError && isComplete;
    const showError = !isProcessing && hasError;
    if (isProcessing) {
      return <Loader className="h-4 w-4 animate-spin text-blue-600 flex-shrink-0" />;
    }
    if (showSuccess) {
      return <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />;
    }
    if (showError) {
      return <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />;
    }
    return null;
  }, [isProcessing, hasError, isComplete, logs.length]);

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-5 sm:right-5 z-[9999] flex flex-col items-end max-w-[calc(100vw-1rem)]">
      {expanded ? (
        <div
          className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden flex flex-col w-[min(320px,calc(100vw-2rem))]"
          style={EXPANDED_PANEL_STYLE}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
            <span className="text-xs font-semibold text-gray-700">
              Processing log
            </span>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={onClear}
                className="p-1.5 rounded text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                title="Clear log"
                aria-label="Clear log"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>

              <button
                type="button"
                onClick={onCollapse}
                className="p-1.5 rounded text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                title="Collapse"
                aria-label="Collapse log panel"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-hidden min-h-0">
            <StatusPanel
              logs={logs}
              isProcessing={isProcessing}
              isComplete={isComplete}
              hasError={hasError}
              compact
            />
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-3 py-2 flex items-center gap-2 w-full max-w-[min(280px,calc(100vw-2rem))]">
          {statusIcon}

          <button
            type="button"
            onClick={onExpand}
            className="text-xs text-gray-700 truncate flex-1 min-w-0 text-left hover:text-gray-900"
            title="Expand to see full log"
          >
            {collapsedPreviewText}
          </button>

          {logs.length > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              title="Clear log"
              aria-label="Clear log"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}

          <button
            type="button"
            onClick={onExpand}
            className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            title="Expand"
            aria-label="Expand log panel"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export default React.memo(FloatingProcessingLogPanel);
